import { Component, Injector, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
    CreateOrganizationChecklistDto,
    GetOrganizationChecklistDto,
    OrganizationChecklistActions,
    OrganizationChecklistState,
    TranslatedChecklist,
    UpdateOrganizationChecklistDto,
    selectChecklistById,
} from '@modeso/aumico-lib-annualreport-fe';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store, select } from '@ngrx/store';

import { ofType } from '@ngrx/effects';
import { Observable, take, tap } from 'rxjs';
import { selectOrganizationChecklistLoadingState } from '../../../../../../../../aumico-lib-users-fe/src/lib';
import { Paths } from '../../../../../enums/paths.enum';
import { BasePageComponent } from '../../../../../pages/base.page';

@UntilDestroy()
@Component({
    selector: 'app-add-edit-checklist',
    templateUrl: './add-edit-checklist.component.html',
    styleUrls: ['./add-edit-checklist.component.scss'],
})
export class AddEditChecklistPage extends BasePageComponent implements OnInit {
    userLanguage: string;
    checklist$: Observable<GetOrganizationChecklistDto>;
    checklist: GetOrganizationChecklistDto;
    isSubmitting: boolean;
    constructor(
        private store: Store<OrganizationChecklistState>,
        private injector: Injector,
        private readonly route: ActivatedRoute,
    ) {
        super(injector);
        this.userLanguage = this.i18n.language.toLowerCase();
        if (this.route.snapshot.params['id'] != null) {
            const id = this.route.snapshot.params['id'];
            this.checklist$ = this.store.pipe(
                select(selectChecklistById(id)),
                tap((checklist) => {
                    if (checklist == null) {
                        this.store.dispatch(OrganizationChecklistActions.loadChecklistById({ id }));
                    }

                    this.checklist = checklist;
                }),
            );
        }
    }

    override ngOnInit(): void {
        // Listen for success or failure actions related to checklist creation or update
        this.store
            .select(selectOrganizationChecklistLoadingState) // Modify selector based on your state structure
            .pipe(untilDestroyed(this))
            .subscribe((isLoading) => {
                if (!isLoading) {
                    this.isSubmitting = false; // Reset the flag on success or failure
                }
            });
    }

    onSubmitChecklistForm($event: Partial<TranslatedChecklist>): void {
        if (this.isSubmitting) {
            return; // Prevent multiple submissions
        }
        this.isSubmitting = true; // Set the flag to indicate submission is in progress
        const checklistDto = this.mapTranslatedChecklistToCreateOrganizationChecklistDto($event);
        if ($event.id && !this.checklist?.isDefault) {
            const updateChecklistDto = checklistDto as UpdateOrganizationChecklistDto;
            this.store.dispatch(OrganizationChecklistActions.updateChecklist({ id: $event.id, updateChecklistDto }));
        } else {
            const createChecklistDto = checklistDto as CreateOrganizationChecklistDto;
            this.store.dispatch(OrganizationChecklistActions.createChecklist({ createChecklistDto }));
        }

        this.actionsListener$
            .pipe(
                ofType(
                    OrganizationChecklistActions.updateChecklistSuccess,
                    OrganizationChecklistActions.createChecklistSuccess,
                ),
                take(1),
            )
            .subscribe(() => {
                this.router.navigateByUrl(
                    `${this.userLanguage}/${Paths.HOME}/${Paths.ORGANIZATION}/${Paths.SETTINGS}/${Paths.CHECKLISTS}`,
                );

                this.fireSuccessNotification({
                    titleKey: this.i18n.t('aumico_reports_notification_success_title') as string,
                    descriptionKey: this.i18n.t('aumico_report_save_success_notification_description') as string,
                });
            });

        this.actionsListener$
            .pipe(
                ofType(
                    OrganizationChecklistActions.updateChecklistFailure,
                    OrganizationChecklistActions.createChecklistFailure,
                ),
                take(1),
            )
            .subscribe(() => {
                this.fireErrorMessage();
            });
    }

    onCancelCheckList(): void {
        this.router.navigateByUrl(
            `${this.userLanguage}/${Paths.HOME}/${Paths.ORGANIZATION}/${Paths.SETTINGS}/${Paths.CHECKLISTS}`,
        );
    }

    private mapTranslatedChecklistToCreateOrganizationChecklistDto(
        $event: Partial<TranslatedChecklist>,
    ): CreateOrganizationChecklistDto | UpdateOrganizationChecklistDto {
        const { name, tasks } = $event;

        if ($event.id && !this.checklist?.isDefault) {
            return {
                ...this.checklist,
                tasks,
                name,
            } as UpdateOrganizationChecklistDto;
        } else if ($event.id && this.checklist?.isDefault) {
            return {
                name,
                tasks,
                flis: this.checklist.flis,
                originalChecklistId: this.checklist.id,
            } as CreateOrganizationChecklistDto;
        } else {
            return {
                name,
                tasks,
            } as CreateOrganizationChecklistDto;
        }
    }
}
